import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { MasterDetail } from '../lib/MasterDetail';
import { useUrlState } from '../lib/urlState';
import { usePermissions } from '../auth/store';
import { ListenerDetailView } from '../pages/listeners/ListenerDetailPage';
import { UserDetailView } from '../pages/users/UserDetailView';

/**
 * Person details beside ANY page. A listener or user name clicked on Calls,
 * Recharges, Payouts, the dashboard… opens their complete detail in a pane on
 * the right of the page the admin is on (`?listener=` / `?user=`, tab in
 * `?dtab=`), instead of navigating to the Listeners / Users page.
 *
 * The Listeners and Users pages own those params themselves (their list is
 * the master), so here they are left to them. One person at a time: the
 * in-place links clear the other param when they set theirs.
 */
export function PersonPane({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { get, set } = useUrlState();
  const { can } = usePermissions();
  const listenerId = pathname !== '/listeners' && can('listeners.view') ? get('listener') : null;
  const userId = !listenerId && pathname !== '/users' && can('users.detail.view') ? get('user') : null;
  const close = () => set({ listener: null, user: null, dtab: null });
  return (
    <MasterDetail
      open={!!(listenerId || userId)}
      onClose={close}
      chrome={false}
      listWidth="50%"
      drawerWidth={960}
      list={children}
    >
      {listenerId ? (
        <ListenerDetailView id={listenerId} onClose={close} tabParam="dtab" />
      ) : userId ? (
        <UserDetailView id={userId} onClose={close} />
      ) : null}
    </MasterDetail>
  );
}
